import i18next, {InitOptions, TOptions} from 'i18next';

import enTranslates from '@/../strings/generated/en.json';
import {User} from '@/domain/models/User';
import {
  APP_LANGUAGE_SUPPORTS,
  AppLanguage,
} from '@/shared/lib/appLanguageSupports';
import {
  getManualAndAppLanguage,
  setManualAndAppLanguage,
} from '@/shared/lib/localStorage/i18n/manualAndAppLanguage';

import BrowserLanguageDetector from './BrowserLanguageDetector';
import {getGenderContext} from './getGenderContext';
import {detectLanguage} from './lib/detectLanguage';
import {getBrowserLanguagePreferences} from './lib/getBrowserLanguagePreferences';
import {getLanguageFromUrl} from './lib/getLanguageFromUrl';
import ResourceLoader from './ResourceLoader';

// Global interpolation defaults, mirroring the mobile app's `appContext`.
// Keys containing {{appName}}, {{supportEmail}} or {{subscriptionName}}
// resolve everywhere without callers passing these values.
const globalVariables = {
  appName: 'Bumpy',
  supportEmail: 'support@bumpy.app',
  subscriptionName: 'Gold',
};

type WithoutPluralsAndContext<K> = K extends `${infer B}_${
  | 'zero'
  | 'one'
  | 'two'
  | 'few'
  | 'many'
  | 'other'
  | 'male'
  | 'female'
  | 'transMale'
  | 'transFemale'
  | 'nonBinary'}`
  ? B | K
  : K;

export type LocalizationKey = WithoutPluralsAndContext<
  keyof typeof enTranslates
>;

type LocalizationFunc = (
  key: LocalizationKey,
  options?: TOptions & {
    user?: Pick<User, 'gender'>;
    // in mobile app it used for 3 times for specific context
    genderTransContext?: true;
  }
) => string;

export const t: LocalizationFunc = (
  key,
  {user, genderTransContext, ...options} = {}
) => {
  if (user) {
    if (!options.context) {
      options.context = '';
    }

    if (options.context && i18next.options.contextSeparator) {
      options.context += i18next.options.contextSeparator;
    }

    options.context += getGenderContext(user.gender, genderTransContext);
  }

  return i18next.t(key, {...globalVariables, ...options});
};

const commonOptions = {
  supportedLngs: Object.keys(APP_LANGUAGE_SUPPORTS),
  fallbackLng: 'en',
  // i18next canonicalizes tags carrying a region or script through
  // `Intl.getCanonicalLocales` (`pt-br` -> `pt-BR`, `zh-hans` -> `zh-Hans`),
  // then matches the result against `supportedLngs` verbatim. Our keys — and
  // the URL segments generated from them — are lowercase, so without this the
  // canonical form is rejected, the language silently resolves to `en`, and
  // the backend is never asked for the bundle at all.
  lowerCaseLng: true,
  partialBundledLanguages: true,
  ns: 'translation',
  defaultNS: 'translation',
  resources: {
    en: {
      translation: enTranslates,
    },
  },
} satisfies InitOptions;

export async function init() {
  await i18next
    .use(new ResourceLoader())
    .use(new BrowserLanguageDetector())
    .init({
      ...commonOptions,
      detection: {
        order: ['path', 'localStorage'],
      },
    });
}

/**
 * Guards the window in which the process-global i18next instance is pinned to
 * one language. Incremented before the first `await` so two overlapping calls
 * can never both observe it as free.
 */
let currentRenderingLang: string | undefined;

/**
 * i18n for one prerendered (SSG) path, called from the root route's
 * `middleware` with the render as `next`.
 *
 * `t()` reads a process-global i18next instance, so the language has to be set
 * before the tree renders and must not move until the tree is done. That holds
 * only while renders are strictly sequential — which they are, because React
 * Router prerenders with `concurrency: 1` unless the `prerender` config opts
 * out. Rather than trust that silently, overlap throws: measured at
 * `concurrency: 8`, sharing the singleton mistranslated 7 of 28 pages (Czech
 * rendered in Greek, Spanish in French) with no error of its own.
 *
 * Making this genuinely concurrency-safe means giving each render its own
 * instance, which means injecting i18n through React rather than importing a
 * module-level `t` — a real migration, and a prerequisite for `ssr: true`.
 */
export async function withRequestI18n<T>(
  request: Request,
  render: () => T | Promise<T>
): Promise<T> {
  if (!import.meta.env.SSR) {
    throw new Error('SSR is missing');
  }

  const language = getLanguageFromUrl(request.url) ?? 'en';

  if (currentRenderingLang && currentRenderingLang !== language) {
    throw new Error(
      `i18n: a prerender is already in flight while switching from "${currentRenderingLang}" to "${language}". ` +
        'Renders share one global i18next instance, so overlapping ones ' +
        'mistranslate each other. Drop `concurrency` from the `prerender` ' +
        'config, or move i18n into React context before enabling SSR.'
    );
  }

  currentRenderingLang = language;

  try {
    const {default: ServerResourceLoader} = await import(
      './ServerResourceLoader'
    );

    if (!i18next.isInitialized) {
      await i18next.use(new ServerResourceLoader()).init(commonOptions);
    }

    if (i18next.language !== language) {
      await i18next.changeLanguage(language);
    }

    return await render();
  } finally {
    currentRenderingLang = undefined;
  }
}

/**
 * Re-detects the language for `url` and moves i18next if it disagrees.
 *
 * i18next consults a language detector only from inside `changeLanguage()` with
 * no argument, which happens once per `init()` — and the client initialises once
 * per document load. Client navigation swaps the URL through the History API
 * without re-initialising, so nothing re-detects on its own: opening `/` and
 * then navigating to `/app` keeps the `en` that `/` resolved to instead of the
 * language stored for the app. Call this whenever the location changes.
 */
export function syncLanguageWithUrl(url: string) {
  const language = detectLanguage(url, getBrowserLanguagePreferences());

  if (i18next.language === language) {
    return;
  }

  return i18next.changeLanguage(language);
}

export function getLanguage() {
  return i18next.language as AppLanguage;
}

export function setLanguage(language: AppLanguage) {
  setManualAndAppLanguage(language);
  return i18next.changeLanguage(language);
}

export function hasManualAndAppLanguage() {
  return Boolean(getManualAndAppLanguage());
}
